<template>
  <div>
    <!-- Title -->

    <b-tabs content-class="mt-2" class="justify-content-center">
      <!-- TAB 1: Campaign Details -->
      <b-tab :title="$t('Campaign')" active>
        <!-- General Information -->
        <b-card class="mb-1">
          <b-card-title>{{ $t('columns.generalInfo') }}</b-card-title>
          <b-row>
            <b-col cols="12" xl="12" class="mb-1">
              <label>{{ $t('columns.orgId') }}</label>
              <organization-select-box v-model="campaign.orgId"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.campaign') }}</label>
              <b-form-input v-model="campaign.campaign"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.status') }}</label>
              <v-select v-model="campaign.status" :options="statusOptions"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.agencyName') }}</label>
              <b-form-input v-model="campaign.agencyName"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.setNumber') }}</label>
              <b-form-input v-model="campaign.setNumber"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.importance') }}</label>
              <v-select v-model="campaign.importance" :options="importanceOptions"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.priority') }}</label>
              <v-select v-model="campaign.priority" :options="priorityOptions"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.assignedTo') }}</label>
              <b-form-input v-model="campaign.assignedTo"/>
            </b-col>
          </b-row>
        </b-card>

        <!-- Financial Data -->
        <b-card class="mb-1">
          <b-card-title>{{ $t('columns.financialData') }}</b-card-title>
          <b-row>
            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.facebookAccountNumber') }}</label>
              <b-form-input v-model="campaign.facebookAccountNumber"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.dailySpend') }} (€)</label>
              <b-form-input type="number" step="0.01" v-model="campaign.dailySpend"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.pixel') }}</label>
              <b-form-input v-model="campaign.pixel"/>
            </b-col>
          </b-row>
        </b-card>

        <!-- Other Information -->
        <b-card class="mb-1">
          <b-card-title>{{ $t('columns.otherInfo') }}</b-card-title>
          <b-row>
            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.alias') }}</label>
              <b-form-input v-model="campaign.alias"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.keitaroCampaignName') }}</label>
              <b-form-input v-model="campaign.keitaroCampaignName"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.trackingLink') }}</label>
              <b-form-input v-model="campaign.trackingLink"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.catalogLink') }}</label>
              <b-form-input v-model="campaign.catalogLink"/>
            </b-col>

            <b-col cols="12" md="6" class="mb-1">
              <label>{{ $t('columns.facebookOrDrive') }}</label>
              <v-select v-model="campaign.facebookOrDrive" :options="facebookOrDriveOptions"/>
            </b-col>

            <b-col cols="12">
              <label>{{ $t('columns.note') }}</label>
              <b-form-textarea v-model="campaign.note" rows="3"/>
            </b-col>
          </b-row>
        </b-card>
      </b-tab>

      <!-- TAB 2: Brand Details -->
      <b-tab :title="$t('Brands')">
        <b-card>
          <b-row>
            <b-col cols="12">
              <label>{{ $t('Brands') }}</label>

              <!-- Pick ONE brand to add; we immediately add it to the map -->
              <v-select
                  v-model="selectedBrand"
                  :options="filteredBrandOptions"
                  label="name"
                  track-by="id"
                  :clearable="true"
                  :searchable="true"
                  placeholder="Select brand to add"
                  @input="onAddBrand"
              />
            </b-col>
          </b-row>

          <!-- Existing selections from campaign.brandPercentages -->
          <div v-if="selectedBrandIds.length" class="mt-3">
            <b-row
                v-for="bid in selectedBrandIds"
                :key="bid"
                class="align-items-center mb-2"
            >
              <b-col cols="5">
                {{ getBrandName(bid) }}
              </b-col>

              <b-col cols="5" class="d-flex align-items-center">
                <b-form-input
                    :value="campaign.brandPercentages[bid]"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    style="width: 120px"
                    @input="onPercentChange(bid, $event)"
                />
                <span class="ml-1">%</span>
                <small class="text-muted ml-2" v-if="campaign.brandPercentages[bid] == null">
                  ({{ $t('default') }})
                </small>
              </b-col>

              <b-col cols="2">
                <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="removeBrand(bid)"
                    v-b-tooltip.hover
                    :title="$t('operations.delete')"
                >
                  <i class="fa fa-trash"></i>
                </b-button>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-tab>
    </b-tabs>

    <!-- Actions -->
    <div class="text-center mt-2">
      <b-button size="sm" variant="outline-warning" @click="reset">{{ $t('operations.reset') }}</b-button>
      &nbsp;
      <b-button size="sm" variant="outline-success" @click="save">{{ $t('operations.save') }}</b-button>
    </div>
  </div>
</template>

<script>
import Campaign from '@/model/campaign/Campaign';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import { mapActions } from 'vuex';
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox.vue';

export default {
  name: 'CampaignEditDetails',
  components: { OrganizationSelectBox },
  mixins: [mixinNotifications, mixinPermissions],
  data() {
    return {
      campaign: Campaign(),
      selectedBrand: null,     // temporary select value (object)
      availableBrands: [],
      statusOptions: [/* ... */],
      importanceOptions: [/* ... */],
      priorityOptions: ['High', 'Medium', 'Low'],
      facebookOrDriveOptions: ['Direct Facebook', 'Drive']
    };
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    // Normalize all available brands to a stable shape
    brandOptions() {
      return (this.availableBrands || []).map(b => {
        const id = String(b.id ?? b._id ?? b.value ?? '');
        const name = b.name ?? b.title ?? b.label ?? id;
        const active = b.active === true || b.active === 'true';
        return {
          ...b,
          id,
          name,
          active
        };
      });
    },
    // Only show ACTIVE brands that are NOT already in the map
    filteredBrandOptions() {
      const picked = new Set(this.selectedBrandIds);
      return this.brandOptions.filter(b => b.active && !picked.has(b.id));
    },
    // Keys of the percentages map (what's selected)
    selectedBrandIds() {
      const map = this.campaign.brandPercentages || {};
      return Object.keys(map)
          .map(String);
    },
  },
  watch: {
    // Ensure campaign.brandPercentages exists & normalize keys/values
    value: {
      handler(val) {
        // keep your existing assign logic
        this.campaign = Object.assign(Campaign(), val);

        if (!this.campaign.brandPercentages || typeof this.campaign.brandPercentages !== 'object') {
          this.$set(this.campaign, 'brandPercentages', {});
        } else {
          // normalize keys to strings; keep numbers or nulls
          const clean = {};
          for (const [k, v] of Object.entries(this.campaign.brandPercentages)) {
            clean[String(k)] = (v === '' || v == null) ? null : Number(v);
          }
          this.campaign.brandPercentages = clean;
        }
      },
      immediate: true
    }
  },
  created() {
    this.fetchBrands();
  },
  methods: {
    ...mapActions('campaign', ['createCampaign', 'updateCampaign']),
    ...mapActions('brand', ['getAll']),
    onAddBrand(brandObj) {
      if (!brandObj) return;
      const id = String(brandObj.id);
      if (!this.campaign.brandPercentages) {
        this.$set(this.campaign, 'brandPercentages', {});
      }
      if (!this.campaign.brandPercentages.hasOwnProperty(id)) {
        this.$set(this.campaign.brandPercentages, id, null);
      }
      // clear the select so you can add another
      this.selectedBrand = null;
    },

    onPercentChange(id, raw) {
      const val = (raw === '' || raw == null) ? null : Number(raw);
      this.$set(this.campaign.brandPercentages, String(id), isNaN(val) ? null : val);
    },

    removeBrand(id) {
      const sid = String(id);
      if (this.campaign.brandPercentages && this.campaign.brandPercentages.hasOwnProperty(sid)) {
        this.$delete(this.campaign.brandPercentages, sid);
      }
    },
    async fetchBrands() {
      let $this = this;

      const result = await this.getAll()
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.brands') }), $this.getErrorDescription(err));
            $this.setLoaded(loadingToken);
          });
      console.log('refresh()', result);

      this.availableBrands = result.data;

    },
    getBrandName(id) {
      const b = this.availableBrands.find(br => br.id === id);
      return b ? b.name : id;
    },
    async save() {

      if (!this.campaign.id) {
        await this.createCampaign({ campaign: this.campaign });
        this.showSuccessNotification(this.$t('globalMessages.addDataSuccess', { system: this.$t('menu.campaigns') }));
      } else {
        await this.updateCampaign({
          campaign: this.campaign,
          id: this.campaign.id
        });
        this.showSuccessNotification(this.$t('globalMessages.updateDataSuccess', { system: this.$t('menu.campaigns') }));
      }
      this.refreshTables();
      this.$emit('input', this.campaign);
      this.$emit('close');
    },
    reset() {
      this.campaign = Campaign();
      this.brandPercentages = {};
    }
  }
};
</script>
